<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Textarea Component
      p.text--secondary A versatile textarea component with multiple states and configurations for multi-line text input.

    // Textarea States
    .component-section.mb-8#basic-states
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-states')") Basic States
      .variations-grid
        .variation-item.mb-6(v-for="variation in textareaVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          // Grouped textareas (multiple examples)
          template(v-if="variation.isGroup")
            .variation-preview.mb-4.pa-4.border.rounded
              .d-flex.flex-column
                div(v-for="field in variation.fields" :key="field.label" class="mb-4")
                  div.mb-2
                    span.font-weight-medium {{ field.label }}
                    small.text--secondary.ml-2(v-if="field.hint") {{ field.hint }}
                  yz-textarea(:value="field.value" @input="(val) => $set(fieldValues, field.key, val)" v-bind="field.props" :style="variation.id === 'width-control' ? {} : { maxWidth: '400px' }")
                  .mt-2.text-caption.text--secondary
                    strong v-model value: 
                    code.pa-1.rounded(style="background-color: #f0f0f0;") "{{ field.value }}"

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                div(v-for="field in variation.fields" :key="field.label" class="mb-3")
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === field.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === field.code ? 'success' : ''" @click="copyCode(field.code)")
                    span.font-weight-medium.ml-2 {{ field.label }}
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ field.code }}

          // V-form integration examples
          template(v-else-if="variation.id === 'vform-integration'")
            .variation-preview.mb-4.pa-4.border.rounded
              .mb-4
                h5.mb-2 V-form with Multiple Textareas
                v-form(ref="demoTextareaForm")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Description
                    yz-textarea(v-model="vformDescriptionValue" placeholder="Enter description..." required, required-error-message="Description is required" :rules="[v => (v && v.length >= 10) || 'Description must be at least 10 characters']" clearable :style="{ maxWidth: '400px' }" :rows="3")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Comments
                    yz-textarea(v-model="vformCommentsValue" placeholder="Enter comments..." required, required-error-message="Comments are required" :rules="[v => (v && v.length >= 5) || 'Comments must be at least 5 characters']" clearable :style="{ maxWidth: '400px' }" :rows="4")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Additional Notes (Optional)
                    yz-textarea(v-model="vformNotesValue" placeholder="Enter additional notes..." :rules="[v => !v || v.length <= 50 || 'Notes must be less than 50 characters']" clearable :style="{ maxWidth: '400px' }" :rows="3")
                  .mt-4
                    .d-flex.gap-2
                      yz-btn(label="Reset" size="sm" @click="resetTextareaVForm")
                      yz-btn(label="Validate" size="sm" outlined @click="validateTextareaVForm")
                    .mt-2(v-if="vformTextareaValidationResult")
                      span.yz-regular-body-xs(:class="{ 'success--text': vformTextareaValidationResult === 'Valid', 'error--text': vformTextareaValidationResult === 'Invalid' }") {{ vformTextareaValidationResult }}
                  .mt-3
                    small.text--secondary Form Values: {{ JSON.stringify({ description: vformDescriptionValue, comments: vformCommentsValue, notes: vformNotesValue }) }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                .mb-3
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === vformTextareaExampleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === vformTextareaExampleCode ? 'success' : ''" @click="copyCode(vformTextareaExampleCode)")
                    span.font-weight-medium.ml-2 V-form Integration Example
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ vformTextareaExampleCode }}

          // Rules-based validation examples
          template(v-else-if="variation.id === 'rules-validation'")
            .variation-preview.mb-4.pa-4.border.rounded
              .mb-4
                h5.mb-2 Custom Validation Rules for Textareas
                v-form(ref="rulesTextareaValidationForm")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Product Review
                    yz-textarea(v-model="rulesReviewValue" placeholder="Write your product review..." :rules="customReviewRules" clearable :style="{ maxWidth: '400px' }" :rows="4")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Feedback Message
                    yz-textarea(v-model="rulesFeedbackValue" placeholder="Enter your feedback..." :rules="feedbackRules" clearable :style="{ maxWidth: '400px' }" :rows="3")
                .mt-4
                  .d-flex.gap-2
                    yz-btn(label="Reset" size="sm" @click="resetTextareaRulesValidation")
                    yz-btn(label="Validate" size="sm" outlined @click="validateTextareaRulesFields")
                  .mt-2(v-if="rulesTextareaValidationResult")
                    span.yz-regular-body-xs(:class="{ 'success--text': rulesTextareaValidationResult === 'Valid', 'error--text': rulesTextareaValidationResult === 'Invalid' }") {{ rulesTextareaValidationResult }}
                .mt-3
                  small.text--secondary Values: {{ JSON.stringify({ review: rulesReviewValue, feedback: rulesFeedbackValue }) }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                .mb-3
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === rulesTextareaExampleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === rulesTextareaExampleCode ? 'success' : ''" @click="copyCode(rulesTextareaExampleCode)")
                    span.font-weight-medium.ml-2 Custom Rules Example
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ rulesTextareaExampleCode }}

          // Optional fields with conditional validation
          template(v-else-if="variation.id === 'optional-validation'")
            .variation-preview.mb-4.pa-4.border.rounded
              .mb-4
                h5.mb-2 Optional Textareas with Smart Validation
                p.text--secondary.mb-3 These textareas are optional but validate format when values are entered
                v-form(ref="optionalTextareaValidationForm")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Bio (Optional)
                    yz-textarea(v-model="optionalBioValue" placeholder="Tell us about yourself..." :rules="optionalBioRules" clearable :style="{ maxWidth: '400px' }" :rows="4")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Address (Optional)
                    yz-textarea(v-model="optionalAddressValue" placeholder="Enter your address..." :rules="optionalAddressRules" clearable :style="{ maxWidth: '400px' }" :rows="3")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Special Instructions (Optional)
                    yz-textarea(v-model="optionalInstructionsValue" placeholder="Any special instructions..." :rules="optionalInstructionsRules" clearable :style="{ maxWidth: '400px' }" :rows="3")
                .mt-4
                  .d-flex.gap-2
                    yz-btn(label="Reset" size="sm" @click="resetOptionalTextareaFields")
                    yz-btn(label="Validate" size="sm" outlined @click="validateOptionalTextareaFields")
                  .mt-2(v-if="optionalTextareaValidationResult")
                    span.yz-regular-body-xs(:class="{ 'success--text': optionalTextareaValidationResult === 'Valid', 'error--text': optionalTextareaValidationResult === 'Invalid' }") {{ optionalTextareaValidationResult }}
                .mt-3
                  small.text--secondary Values: {{ JSON.stringify({ bio: optionalBioValue, address: optionalAddressValue, instructions: optionalInstructionsValue }) }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                .mb-3
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === optionalTextareaValidationExampleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalTextareaValidationExampleCode ? 'success' : ''" @click="copyCode(optionalTextareaValidationExampleCode)")
                    span.font-weight-medium.ml-2 Optional Validation Example
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ optionalTextareaValidationExampleCode }}

          // Individual textarea
          template(v-else)
            .variation-preview.mb-4.pa-4.border.rounded
              .mb-2
                span.font-weight-medium {{ variation.label }}
                small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
              yz-textarea(v-model="variation.value" v-bind="variation.props" :style="variation.id === 'width-control' ? {} : { maxWidth: '400px' }")

            .variation-code
              .code-header.d-flex.align-center.mb-2
                yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
                span.font-weight-medium.ml-2 Code

              pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in textareaProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h4.mb-3 Events
      .events-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Parameters
                th Description
            tbody
              tr(v-for="event in textareaEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.parameters }}
                td.single-line {{ event.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      fieldValues: {
        basic: '',
        disabled: 'This textarea is disabled',
        readonly: 'This is read-only content that cannot be modified',
        error: 'This text has validation errors',
        loading: 'Processing your input...',
        clearable: 'This textarea has a clear button',
        autofocus: 'This textarea is focused on load',
        counterBasic: 'Hello world! This is some sample text.',
        counterMax: 'This text is limited to a maximum length',
        counterValidation: 'Valid text content',
        rowsFixed: 'This textarea has a fixed height of 3 rows',
        rowsAuto: 'This textarea grows automatically as you type more content',
        noResize: 'This textarea cannot be resized by the user',
        prependIcon: 'Search through your notes...',
        appendIcon: 'Write your message here',
        prefix: 'Dear valued customer,',
        suffix: 'Best regards, Support Team',
        combined: 'Enter your detailed feedback here...',
        colorDefault: 'Default white background textarea',
        colorHex: 'Light gray background textarea',
        colorYzToken: 'Light blue background textarea',
        colorCssVar: 'Custom CSS variable color textarea',
        colorShort: 'Neutral background textarea',
        widthPixel: 'Fixed 300px width textarea',
        widthPercent: '50% of container textarea',
        widthFull: 'Full width textarea',
        widthRem: '20rem width textarea',
        widthFitContent: 'Fits content width textarea'
      },
      // V-form validation data
      vformDescriptionValue: '',
      vformCommentsValue: '',
      vformNotesValue: '',
      vformTextareaValidationResult: null,
      // Rules-based validation data
      rulesReviewValue: '',
      rulesFeedbackValue: '',
      rulesTextareaValidationResult: null,
      // Optional fields with conditional validation data
      optionalBioValue: '',
      optionalAddressValue: '',
      optionalInstructionsValue: '',
      optionalTextareaValidationResult: null,
      // Code examples for new sections
      vformTextareaExampleCode: `// Template
v-form(ref="demoTextareaForm")
  .mb-3
    label Description
    yz-textarea(
      v-model="descriptionValue" 
      placeholder="Enter description..." 
      required 
      required-error-message="Description is required"
      :rules="descriptionRules"
      clearable
      :rows="3"
    )
  .mb-3
    label Comments
    yz-textarea(
      v-model="commentsValue" 
      placeholder="Enter comments..." 
      required
      required-error-message="Comments are required"
      :rules="commentRules" 
      clearable
      :rows="4"
    )
  .mb-3
    label Additional Notes (Optional)
    yz-textarea(
      v-model="notesValue" 
      placeholder="Enter additional notes..." 
      :rules="notesRules" 
      clearable
      :rows="3"
    )
  .mt-4
    yz-btn(label="Validate Form" @click="validateForm")
    yz-btn(label="Reset Form" @click="resetForm")

// Script
data() {
  return {
    descriptionValue: '',
    commentsValue: '',
    notesValue: ''
  }
},
computed: {
  descriptionRules() {
    return [
      v => (v && v.length >= 10) || 'Description must be at least 10 characters'
    ]
  },
  commentRules() {
    return [
      v => (v && v.length >= 5) || 'Comments must be at least 5 characters'
    ]
  },
  notesRules() {
    return [
      v => !v || v.length <= 50 || 'Notes must be less than 50 characters'
    ]
  }
},
methods: {
  validateForm() {
    const isValid = this.$refs.demoTextareaForm.validate();
    console.log('Form is valid:', isValid);
  },
  resetForm() {
    this.$refs.demoTextareaForm.resetValidation();
  }
}`,
      rulesTextareaExampleCode: `// Template
yz-textarea(
  v-model="reviewValue" 
  placeholder="Write your product review..." 
  :rules="customReviewRules" 
  clearable
  :rows="4"
)

yz-textarea(
  v-model="feedbackValue" 
  placeholder="Enter your feedback..." 
  :rules="feedbackRules" 
  clearable
  :rows="3"
)

// Script
data() {
  return {
    reviewValue: '',
    feedbackValue: ''
  }
},
computed: {
  customReviewRules() {
    return [
      v => !!v || 'Review is required',
      v => (v && v.length >= 20) || 'Review must be at least 20 characters',
      v => (v && v.length <= 500) || 'Review must be less than 500 characters',
      v => !/^\\s*$/.test(v) || 'Review cannot be only whitespace'
    ]
  },
  feedbackRules() {
    return [
      v => !!v || 'Feedback is required',
      v => (v && v.length >= 10) || 'Feedback must be at least 10 characters',
      v => (v && v.split(' ').length >= 3) || 'Feedback must contain at least 3 words'
    ]
  }
}`,
      optionalTextareaValidationExampleCode: `// Template
// Optional Bio
yz-textarea(
  v-model="bioValue" 
  placeholder="Tell us about yourself..." 
  :rules="optionalBioRules" 
  clearable
  :rows="4"
)

// Optional Address
yz-textarea(
  v-model="addressValue" 
  placeholder="Enter your address..." 
  :rules="optionalAddressRules" 
  clearable
  :rows="3"
)

// Optional Special Instructions
yz-textarea(
  v-model="instructionsValue" 
  placeholder="Any special instructions..." 
  :rules="optionalInstructionsRules" 
  clearable
  :rows="3"
)

// Script
data() {
  return {
    bioValue: '',
    addressValue: '',
    instructionsValue: ''
  }
},
computed: {
  // Optional fields only validate when value is provided
  optionalBioRules() {
    return [
      v => !v || v.length >= 20 || 'Bio must be at least 20 characters if provided',
      v => !v || v.length <= 300 || 'Bio must be less than 300 characters'
    ]
  },
  optionalAddressRules() {
    return [
      v => !v || v.length >= 10 || 'Address must be at least 10 characters if provided'
    ]
  },
  optionalInstructionsRules() {
    return [
      v => !v || v.length <= 200 || 'Instructions must be less than 200 characters'
    ]
  }
}`
    }
  },
  computed: {
    textareaVariations() {
      return [
        {
          name: 'Basic States',
          id: 'basic-textareas',
          description: 'Standard textarea states including normal, disabled, read-only, and error',
          isGroup: true,
          fields: [
            {
              label: 'Basic',
              key: 'basic',
              props: {
                placeholder: 'Enter your text here...',
                rows: 3
              },
              value: this.fieldValues.basic,
              code: `yz-textarea(v-model="value" placeholder="Enter your text here..." :rows="3")`,
              hint: 'Standard multi-line text input'
            },
            {
              label: 'Disabled',
              key: 'disabled',
              props: {
                placeholder: 'This textarea is disabled',
                disabled: true,
                rows: 3
              },
              value: this.fieldValues.disabled,
              code: `yz-textarea(v-model="value" placeholder="This textarea is disabled" disabled :rows="3")`,
              hint: 'Cannot be edited'
            },
            {
              label: 'Read-only',
              key: 'readonly',
              props: {
                readonly: true,
                rows: 3
              },
              value: this.fieldValues.readonly,
              code: `yz-textarea(v-model="value" readonly :rows="3")`,
              hint: 'Cannot be modified'
            },
            {
              label: 'Error State',
              key: 'error',
              props: {
                placeholder: 'Enter valid text...',
                errorMessages: ['This field is required', 'Please enter valid content'],
                error: true,
                rows: 3
              },
              value: this.fieldValues.error,
              code: `yz-textarea(v-model="value" placeholder="Enter valid text..." :error-messages="['This field is required', 'Please enter valid content']" error :rows="3")`,
              hint: 'Shows error state'
            },
            {
              label: 'Loading',
              key: 'loading',
              props: {
                placeholder: 'Processing...',
                loading: true,
                rows: 3
              },
              value: this.fieldValues.loading,
              code: `yz-textarea(v-model="value" placeholder="Processing..." loading :rows="3")`,
              hint: 'Shows loading indicator'
            },
            {
              label: 'Clearable',
              key: 'clearable',
              props: {
                placeholder: 'Enter text to clear...',
                clearable: true,
                rows: 3
              },
              value: this.fieldValues.clearable,
              code: `yz-textarea(v-model="value" placeholder="Enter text to clear..." clearable :rows="3")`,
              hint: 'Shows clear button when text exists'
            },
            {
              label: 'Autofocus',
              key: 'autofocus',
              props: {
                placeholder: 'This textarea is auto-focused...',
                autofocus: true,
                rows: 3
              },
              value: this.fieldValues.autofocus,
              code: `yz-textarea(v-model="value" placeholder="This textarea is auto-focused..." autofocus :rows="3")`,
              hint: 'Automatically focuses on page load'
            }
          ]
        },
        {
          name: 'Textarea Sizing',
          id: 'textarea-sizing',
          description: 'Different sizing options for textarea height and resizing behavior',
          isGroup: true,
          fields: [
            {
              label: 'Fixed Rows (3)',
              key: 'rowsFixed',
              props: {
                placeholder: 'Fixed height textarea...',
                rows: 3
              },
              value: this.fieldValues.rowsFixed,
              code: `yz-textarea(v-model="value" placeholder="Fixed height textarea..." :rows="3")`,
              hint: 'Fixed height with 3 rows'
            },
            {
              label: 'Auto Grow',
              key: 'rowsAuto',
              props: {
                placeholder: 'Auto-growing textarea...',
                autoGrow: true,
                rows: 2
              },
              value: this.fieldValues.rowsAuto,
              code: `yz-textarea(v-model="value" placeholder="Auto-growing textarea..." auto-grow :rows="2")`,
              hint: 'Grows automatically with content'
            },
            {
              label: 'No Resize',
              key: 'noResize',
              props: {
                placeholder: 'Non-resizable textarea...',
                noResize: true,
                rows: 4
              },
              value: this.fieldValues.noResize,
              code: `yz-textarea(v-model="value" placeholder="Non-resizable textarea..." no-resize :rows="4")`,
              hint: 'User cannot resize manually'
            }
          ]
        },
        {
          name: 'Counter',
          id: 'counter',
          description: 'Textareas with character counting functionality',
          isGroup: true,
          fields: [
            {
              label: 'Basic Counter',
              key: 'counterBasic',
              props: {
                placeholder: 'Type to see character count...',
                counter: true,
                rows: 3
              },
              value: this.fieldValues.counterBasic,
              code: `yz-textarea(v-model="value" placeholder="Type to see character count..." counter :rows="3")`,
              hint: 'Shows current character count'
            },
            {
              label: 'Counter with Max Length',
              key: 'counterMax',
              props: {
                placeholder: 'Maximum 100 characters...',
                counter: 100,
                maxlength: 100,
                rows: 3
              },
              value: this.fieldValues.counterMax,
              code: `yz-textarea(v-model="value" placeholder="Maximum 100 characters..." :counter="100" maxlength="100" :rows="3")`,
              hint: 'Shows count with maximum limit'
            },
            {
              label: 'Counter with Validation',
              key: 'counterValidation',
              props: {
                placeholder: 'Enter 20-80 characters...',
                counter: 80,
                rows: 3,
                rules: [
                  (v) => (v && v.length >= 20) || 'Minimum 20 characters',
                  (v) => (v && v.length <= 80) || 'Maximum 80 characters'
                ]
              },
              value: this.fieldValues.counterValidation,
              code: `yz-textarea(v-model="value" placeholder="Enter 20-80 characters..." :counter="80" :rows="3" :rules="[v => (v && v.length >= 20) || 'Minimum 20 characters', v => (v && v.length <= 80) || 'Maximum 80 characters']")`,
              hint: 'Counter with validation rules'
            }
          ]
        },
        {
          name: 'Icons & Affixes',
          id: 'icons-affixes',
          description: 'Textareas with icons, prefixes, and suffixes for enhanced functionality',
          isGroup: true,
          fields: [
            {
              label: 'Prepend Icon',
              key: 'prependIcon',
              props: {
                placeholder: 'Search through your notes...',
                prependIcon: '$yzi-search-sm',
                rows: 3
              },
              value: this.fieldValues.prependIcon,
              code: `yz-textarea(v-model="value" placeholder="Search through your notes..." prepend-icon="$yzi-search-sm" :rows="3")`,
              hint: 'Icon outside the textarea border'
            },
            {
              label: 'Append Icon',
              key: 'appendIcon',
              props: {
                placeholder: 'Write your message...',
                appendIcon: '$yzi-edit-02',
                rows: 3
              },
              value: this.fieldValues.appendIcon,
              code: `yz-textarea(v-model="value" placeholder="Write your message..." append-icon="$yzi-edit-02" :rows="3")`,
              hint: 'Icon outside the textarea border'
            },
            {
              label: 'Prefix',
              key: 'prefix',
              props: {
                placeholder: 'Enter your message content...',
                prefix: 'Note:',
                rows: 3
              },
              value: this.fieldValues.prefix,
              code: `yz-textarea(v-model="value" placeholder="Enter your message content..." prefix="Note:" :rows="3")`,
              hint: 'Text prefix inside textarea'
            },
            {
              label: 'Suffix',
              key: 'suffix',
              props: {
                placeholder: 'Enter your feedback...',
                suffix: 'End',
                rows: 3
              },
              value: this.fieldValues.suffix,
              code: `yz-textarea(v-model="value" placeholder="Enter your feedback..." suffix="- End" :rows="3")`,
              hint: 'Text suffix inside textarea'
            },
          ]
        },
        {
          name: 'Background Colors',
          id: 'background-colors',
          description: 'Textareas with custom background colors supporting multiple color formats',
          isGroup: true,
          fields: [
            {
              label: 'Default (White)',
              key: 'colorDefault',
              props: {
                placeholder: 'Default white background...',
                backgroundColor: 'white',
                rows: 3
              },
              value: this.fieldValues.colorDefault,
              code: `yz-textarea(v-model="value" placeholder="Default white background..." background-color="white" :rows="3")`,
              hint: 'Default white background color'
            },
            {
              label: 'Hex Color',
              key: 'colorHex',
              props: {
                placeholder: 'Light gray background...',
                backgroundColor: '#f8f9fa',
                rows: 3
              },
              value: this.fieldValues.colorHex,
              code: `yz-textarea(v-model="value" placeholder="Light gray background..." background-color="#f8f9fa" :rows="3")`,
              hint: 'Using hex color format (#f8f9fa)'
            },
            {
              label: 'YZ Design Token',
              key: 'colorYzToken',
              props: {
                placeholder: 'Light blue background...',
                backgroundColor: 'yz-blue-100',
                rows: 3
              },
              value: this.fieldValues.colorYzToken,
              code: `yz-textarea(v-model="value" placeholder="Light blue background..." background-color="yz-blue-100" :rows="3")`,
              hint: 'Using YZ design token (yz-blue-100)'
            },
            {
              label: 'CSS Variable',
              key: 'colorCssVar',
              props: {
                placeholder: 'CSS variable background...',
                backgroundColor: 'var(--yz-green-100)',
                rows: 3
              },
              value: this.fieldValues.colorCssVar,
              code: `yz-textarea(v-model="value" placeholder="CSS variable background..." background-color="var(--yz-green-100)" :rows="3")`,
              hint: 'Using CSS variable format'
            },
            {
              label: 'Short Token',
              key: 'colorShort',
              props: {
                placeholder: 'Neutral background...',
                backgroundColor: 'neutral-200',
                rows: 3
              },
              value: this.fieldValues.colorShort,
              code: `yz-textarea(v-model="value" placeholder="Neutral background..." background-color="neutral-200" :rows="3")`,
              hint: 'Using short design token (neutral-200)'
            }
          ]
        },
        {
          name: 'V-form Integration',
          id: 'vform-integration',
          description: 'Using YzTextarea with Vuetify v-form for comprehensive form validation',
          isGroup: false
        },
        {
          name: 'Rules-based Validation',
          id: 'rules-validation',
          description: 'Custom validation rules with the rules prop for advanced validation scenarios',
          isGroup: false
        },
        {
          name: 'Optional Fields with Conditional Validation',
          id: 'optional-validation',
          description: 'Optional fields that validate only when a value is provided - useful for optional profile fields',
          isGroup: false
        },
        {
          name: 'Width Control',
          id: 'width-control',
          description: 'Textarea with various width configurations using CSS units and predefined sizes',
          isGroup: true,
          fields: [
            {
              label: 'Fixed Pixel Width (300px)',
              key: 'widthPixel',
              props: {
                placeholder: 'Fixed 300px width textarea...',
                width: '300px',
                rows: 3
              },
              value: this.fieldValues.widthPixel,
              code: `yz-textarea(v-model="value" placeholder="Fixed 300px width textarea..." width="300px" :rows="3")`,
              hint: 'Fixed width using pixels'
            },
            {
              label: 'Percentage Width (50%)',
              key: 'widthPercent',
              props: {
                placeholder: '50% of container width textarea...',
                width: '50%',
                rows: 3
              },
              value: this.fieldValues.widthPercent,
              code: `yz-textarea(v-model="value" placeholder="50% of container width textarea..." width="50%" :rows="3")`,
              hint: 'Responsive width as percentage'
            },
            {
              label: 'Full Width',
              key: 'widthFull',
              props: {
                placeholder: 'Takes full container width textarea...',
                width: 'full',
                rows: 3
              },
              value: this.fieldValues.widthFull,
              code: `yz-textarea(v-model="value" placeholder="Takes full container width textarea..." width="full" :rows="3")`,
              hint: 'Predefined full width (100%)'
            },
            {
              label: 'Rem Units (20rem)',
              key: 'widthRem',
              props: {
                placeholder: '20rem width textarea...',
                width: '20rem',
                rows: 3
              },
              value: this.fieldValues.widthRem,
              code: `yz-textarea(v-model="value" placeholder="20rem width textarea..." width="20rem" :rows="3")`,
              hint: 'Width using rem units'
            },
            {
              label: 'Fit Content',
              key: 'widthFitContent',
              props: {
                placeholder: 'Short',
                width: 'fit-content',
                rows: 2
              },
              value: this.fieldValues.widthFitContent,
              code: `yz-textarea(v-model="value" placeholder="Short" width="fit-content" :rows="2")`,
              hint: 'Width adjusts to content size'
            }
          ]
        }
      ]
    },
    textareaProps() {
      return [
        {
          name: 'value',
          type: 'String',
          default: 'null',
          description: 'The textarea value (v-model)'
        },
        {
          name: 'placeholder',
          type: 'String',
          default: 'null',
          description: 'Placeholder text'
        },
        {
          name: 'rows',
          type: 'Number | String',
          default: '5',
          description: 'Number of rows in the textarea'
        },
        {
          name: 'autoGrow',
          type: 'Boolean',
          default: 'false',
          description: 'Automatically adjusts height based on content'
        },
        {
          name: 'noResize',
          type: 'Boolean',
          default: 'false',
          description: 'Prevents manual resizing by the user'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Disables the textarea'
        },
        {
          name: 'readonly',
          type: 'Boolean',
          default: 'false',
          description: 'Makes the textarea read-only'
        },
        {
          name: 'error',
          type: 'Boolean',
          default: 'false',
          description: 'Shows error state styling'
        },
        {
          name: 'errorMessages',
          type: 'Array',
          default: '[]',
          description: 'Array of error messages to display'
        },
        {
          name: 'rules',
          type: 'Array',
          default: '[]',
          description: 'Array of validation rules'
        },
        {
          name: 'hint',
          type: 'String',
          default: 'null',
          description: 'Hint text displayed below the field'
        },
        {
          name: 'prependIcon',
          type: 'String',
          default: 'null',
          description: 'Icon displayed outside the textarea border (before)'
        },
        {
          name: 'appendIcon',
          type: 'String',
          default: 'null',
          description: 'Icon displayed outside the textarea border (after)'
        },
        {
          name: 'prefix',
          type: 'String',
          default: 'null',
          description: 'Text prefix displayed inside the textarea'
        },
        {
          name: 'suffix',
          type: 'String',
          default: 'null',
          description: 'Text suffix displayed inside the textarea'
        },
        {
          name: 'loading',
          type: 'Boolean',
          default: 'false',
          description: 'Shows loading indicator in the textarea'
        },
        {
          name: 'clearable',
          type: 'Boolean',
          default: 'false',
          description: 'Shows clear button when textarea has content'
        },
        {
          name: 'autofocus',
          type: 'Boolean',
          default: 'false',
          description: 'Automatically focuses the textarea on page load'
        },
        {
          name: 'counter',
          type: 'Boolean | Number',
          default: 'false',
          description: 'Shows character count. Use number to set max length'
        },
        {
          name: 'maxlength',
          type: 'Number | String',
          default: 'null',
          description: 'Sets the maximum number of characters allowed'
        },
        {
          name: 'backgroundColor',
          type: 'String',
          default: 'white',
          description: 'Sets the background color of the textarea. Supports multiple formats: hex (#ffffff), CSS variables (var(--yz-color)), YZ design tokens (yz-neutral-100), and short tokens (neutral-100)'
        },
        {
          name: 'width',
          type: 'String',
          default: 'auto',
          description: 'Sets the width of the textarea. Supports CSS units (px, %, rem, em, vw, vh), predefined sizes (full, fit-content), or auto for default width'
        },
        {
          name: 'hideDetails',
          type: 'Boolean | String',
          default: 'auto',
          description: 'Hide error messages and hints. Can be true, false, or "auto"'
        },
        {
          name: 'required',
          type: 'Boolean',
          default: 'false',
          description: 'Makes the textarea required. Automatically adds validation rule. Disabled when field is disabled.'
        },
        {
          name: 'requiredErrorMessage',
          type: 'String',
          default: 'This field is required',
          description: 'Custom error message displayed when required field is empty'
        }
      ]
    },
    textareaEvents() {
      return [
        {
          name: '@input',
          parameters: 'value',
          description: 'Emitted when the textarea value changes'
        },
        {
          name: '@click',
          parameters: 'event',
          description: 'Emitted when the textarea is clicked'
        },
        {
          name: '@click:append',
          parameters: 'event',
          description: 'Emitted when the append icon is clicked'
        },
        {
          name: '@click:prepend',
          parameters: 'event',
          description: 'Emitted when the prepend icon is clicked'
        },
        {
          name: '@focus',
          parameters: 'event',
          description: 'Emitted when the textarea gains focus'
        },
        {
          name: '@blur',
          parameters: 'event',
          description: 'Emitted when the textarea loses focus'
        },
        {
          name: '@keydown',
          parameters: 'event',
          description: 'Emitted when a key is pressed down'
        },
        {
          name: '@keyup',
          parameters: 'event',
          description: 'Emitted when a key is released'
        }
      ]
    },
    customReviewRules() {
      return [
        v => !!v || 'Review is required',
        v => (v && v.length >= 20) || 'Review must be at least 20 characters',
        v => (v && v.length <= 500) || 'Review must be less than 500 characters',
        v => !/^\s*$/.test(v) || 'Review cannot be only whitespace'
      ]
    },
    feedbackRules() {
      return [
        v => !!v || 'Feedback is required',
        v => (v && v.length >= 10) || 'Feedback must be at least 10 characters',
        v => (v && v.split(' ').length >= 3) || 'Feedback must contain at least 3 words'
      ]
    },
    // Optional validation rules - only validate when value is provided
    optionalBioRules() {
      return [
        v => !v || v.length >= 20 || 'Bio must be at least 20 characters if provided',
        v => !v || v.length <= 300 || 'Bio must be less than 300 characters'
      ]
    },
    optionalAddressRules() {
      return [
        v => !v || v.length >= 10 || 'Address must be at least 10 characters if provided'
      ]
    },
    optionalInstructionsRules() {
      return [
        v => !v || v.length <= 200 || 'Instructions must be less than 200 characters'
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      // Update the route with the section hash
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    },
    validateTextareaVForm() {
      if (this.$refs.demoTextareaForm) {
        const demoFormArray = this.$refs.demoTextareaForm;
        const demoFormRef = Array.isArray(demoFormArray) ? demoFormArray[0] : demoFormArray;
        const isValid = demoFormRef.validate();
        this.vformTextareaValidationResult = isValid ? 'Valid' : 'Invalid';
        setTimeout(() => {
          this.vformTextareaValidationResult = null;
        }, 1500);
      }
    },
    resetTextareaVForm() {
      if (this.$refs.demoTextareaForm) {
        const demoFormArray = this.$refs.demoTextareaForm;
        const demoFormRef = Array.isArray(demoFormArray) ? demoFormArray[0] : demoFormArray;
        demoFormRef.reset();
        this.vformTextareaValidationResult = null;
      }
    },
    validateTextareaRulesFields() {
      if (this.$refs.rulesTextareaValidationForm) {
        const rulesFormArray = this.$refs.rulesTextareaValidationForm;
        const rulesFormRef = Array.isArray(rulesFormArray) ? rulesFormArray[0] : rulesFormArray;
        const isValid = rulesFormRef.validate();
        this.rulesTextareaValidationResult = isValid ? 'Valid' : 'Invalid';
        setTimeout(() => {
          this.rulesTextareaValidationResult = null;
        }, 2000);
      }
    },
    resetTextareaRulesValidation() {
      if (this.$refs.rulesTextareaValidationForm) {
        const rulesFormArray = this.$refs.rulesTextareaValidationForm;
        const rulesFormRef = Array.isArray(rulesFormArray) ? rulesFormArray[0] : rulesFormArray;
        rulesFormRef.reset();
        this.rulesTextareaValidationResult = null;
      }
    },
    validateOptionalTextareaFields() {
      if (this.$refs.optionalTextareaValidationForm) {
        const optionalFormArray = this.$refs.optionalTextareaValidationForm;
        const optionalFormRef = Array.isArray(optionalFormArray) ? optionalFormArray[0] : optionalFormArray;
        const isValid = optionalFormRef.validate();
        this.optionalTextareaValidationResult = isValid ? 'Valid' : 'Invalid';
        setTimeout(() => {
          this.optionalTextareaValidationResult = null;
        }, 2000);
      }
    },
    resetOptionalTextareaFields() {
      if (this.$refs.optionalTextareaValidationForm) {
        const optionalFormArray = this.$refs.optionalTextareaValidationForm;
        const optionalFormRef = Array.isArray(optionalFormArray) ? optionalFormArray[0] : optionalFormArray;
        optionalFormRef.reset();
        this.optionalTextareaValidationResult = null;
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-4 > * + * {
  margin-top: 1rem;
}
</style>